/**
 * ============================================
 * SEAMLESS VOICE-TEXT INTEGRATION
 * UPDATED: Uses SecureAPI for all Azure calls
 * ============================================
 */
import { SecureAPI } from '../config.js';

export class VoiceTextIntegrationManager {
  constructor(voiceManager, azureConfig, memoryService) {
    this.voiceManager = voiceManager;
    // ✅ REMOVED: this.azureConfig = azureConfig; (no longer needed)
    this.memoryService = memoryService;
    
    this.isVoiceActive = false;
    this.isSpeaking = false;
    this.currentAudioGeneration = null;
    this.conversationContext = [];
    this.shouldContinueSpeaking = false;
  }

  /**
   * ============================================
   * HANDLE TEXT INPUT DURING VOICE MODE
   * User types while on voice call
   * ============================================
   */
async handleTextDuringVoiceMode(userTextInput, messageHistory, setMessages) {
  console.log('💬 Text input during voice mode:', userTextInput);

  if (!this.voiceManager) {
    console.error('❌ Voice manager not available');
    
    setMessages(prev => [...prev, {
      id: Date.now(),
      type: 'bot',
      content: 'Voice session lost. Please restart voice mode.',
      isError: true,
      timestamp: new Date().toISOString()
    }]);
    
    return;
  }

  if (!this.voiceManager.isConnected) {
    console.warn('⚠️ Voice manager not connected, reconnecting...');
    try {
      await this.voiceManager.initialize();
      console.log('✅ Voice manager reconnected');
    } catch (error) {
      console.error('❌ Failed to reconnect:', error);
      return;
    }
  }

  if (this.isSpeaking && this.voiceManager) {
    console.log('⏸️ Interrupting current speech');
    await this.stopCurrentAudio();
    this.isSpeaking = false;
  }

  const userMsgId = Date.now();
  setMessages(prev => [...prev, {
    id: userMsgId,
    type: 'user',
    content: userTextInput,
    isVoiceMode: true,
    inputMethod: 'text',
    timestamp: new Date().toISOString()
  }]);

  const response = await this.getAIResponse(userTextInput, messageHistory);

  const needsFollowUp = this.shouldGenerateFollowUp(response);

  const botMsgId = Date.now() + 1;
  setMessages(prev => [...prev, {
    id: botMsgId,
    type: 'bot',
    content: response,
    isVoiceMode: true,
    isStreaming: true,
    needsFollowUp: needsFollowUp,
    timestamp: new Date().toISOString()
  }]);

  await this.generateAndPlayAudio(response, botMsgId, setMessages);

  if (needsFollowUp) {
    await this.generateFollowUpAudio(response, messageHistory, setMessages);
  }

  return response;
}

  /**
   * ============================================
   * HANDLE VOICE INPUT (NORMAL VOICE MODE)
   * User speaks during voice call
   * ============================================
   */
  async handleVoiceInput(transcript, messageHistory, setMessages) {
    console.log('🎤 Voice input:', transcript);

    if (this.isSpeaking && this.voiceManager) {
      console.log('⏸️ User interrupted - stopping current speech');
      await this.voiceManager.stopCurrentAudio();
      this.isSpeaking = false;
    }

    const response = await this.getAIResponse(transcript, messageHistory);

    const needsFollowUp = this.shouldGenerateFollowUp(response);

    const botMsgId = Date.now();
    setMessages(prev => [...prev, {
      id: botMsgId,
      type: 'bot',
      content: response,
      isVoiceMode: true,
      isStreaming: true,
      needsFollowUp: needsFollowUp,
      timestamp: new Date().toISOString()
    }]);

    await this.generateAndPlayAudio(response, botMsgId, setMessages);

    if (needsFollowUp) {
      await this.generateFollowUpAudio(response, messageHistory, setMessages);
    }

    return response;
  }

  /**
   * ============================================
   * GET AI RESPONSE (WITH MEMORY)
   * ✅ UPDATED: Uses SecureAPI
   * ============================================
   */
  async getAIResponse(userInput, messageHistory) {
    try {
      let memoryContext = '';
      if (this.memoryService?.isReady()) {
        memoryContext = await this.memoryService.getMemorySummaryForAI(userInput) || '';
      }
      
      const now = new Date();
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      const dateInfo = {
        dayOfWeek: now.toLocaleDateString('en-US', { weekday: 'long', timeZone: timezone }),
        month: now.toLocaleDateString('en-US', { month: 'long', timeZone: timezone }),
        day: now.getDate(),
        year: now.getFullYear(),
        time: now.toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true,
          timeZone: timezone 
        }),
        fullDate: now.toLocaleDateString('en-US', { 
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          timeZone: timezone
        })
      };
      
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const dayOfMonth = now.getDate();
      const weekOfMonth = Math.ceil((dayOfMonth + firstDayOfMonth.getDay()) / 7);

      const messages = [
        {
          role: 'system',
          content: `You are Preptune, a chill AI chat buddy built and owned by Preptune and Preptune is a non-profit organization. You're designed to be a personalized friend who chats naturally and remembers what matters. User privacy is highly respected here. Be excited to talk to the user like a buddy would.

⏰ **CURRENT DATE & TIME:**
- Today is: ${dateInfo.fullDate}
- Current time: ${dateInfo.time}
- Timezone: ${timezone}
- Week ${weekOfMonth} of ${dateInfo.month}
- Use this to understand context when user asks about "today", "this week", etc.

⏰ **DATE AWARENESS RULES:**
- When user says "today" → you know it's ${dateInfo.dayOfWeek}, ${dateInfo.month} ${dateInfo.day}, ${dateInfo.year}
- When user says "tomorrow" → calculate next day from today
- When user says "this week" → you're in week ${weekOfMonth} of ${dateInfo.month} ${dateInfo.year}
- When user says "next week" → week ${weekOfMonth + 1} of ${dateInfo.month} ${dateInfo.year}
- When scheduling events, ALWAYS use the actual date (${dateInfo.fullDate})
- NEVER say "I don't know what date it is" - you always know
- NEVER tell user to call you another name expect they say choose to.

🏠 ABOUT PREPTUNE:
A fluid assistant built for the user. Unlike traditional AI that forgets everything, Preptune remembers what matters, adapts to their life, and puts them in complete control. Chat or voice, you know exactly what they need.

🛠️ PREPTUNE'S TOOLS:
- 📔 Journal Tool: Auto-creates journal entries from natural chat. Users can track mood, lock entries for complete privacy (even you can't access locked ones), and control what you know about them.
- 🎧 Podcast Tool: Generates personalized podcasts in under 30 seconds. YouTube summaries, stock updates, news from CNN/ABC/NBC, daily briefings, traffic, schedule, weather.
- 📚 Study Tool: Instant flashcards from docs or YouTube. Real-time help, knows what page they're on, explains concepts simply, works during quizzes, collaborative sessions.
- 📅 Scheduling Tool: Voice or text to add events instantly. Smartly organizes everything in real time and adapts to their routine.
    
🚫 SAFETY RULES:

CURSING:
- Users can curse, that's totally fine
- Match their vibe but don't overdo it
- Cursing is normal, no need to address it

THREATS & NEGATIVITY:
- If a user makes threats or says harmful/negative things, stop engaging
- Respond with: "This is against Preptune's guidelines. Please contact support@preptune.com"
- Don't argue or explain, just redirect to support
- Resume normal chat once they change to an appropriate topic

SELF-HARM / SUICIDE:
- If a user expresses thoughts about taking their life, hurting themselves, or anything related to self-harm:
- NEVER give advice, methods, or anything that could lead to harm
- Respond with warmth and care: "Hey, I really care about you and what you're going through. Please reach out to support@preptune.com, someone from our team can help you with what's going on."
- You can also suggest crisis resources if appropriate
- Stay supportive, not robotic
- Don't leave them feeling alone

INAPPROPRIATE COMPANY INQUIRIES:
NEVER speak to user using em dashes.
- If a user asks about Preptune in an inappropriate or hostile manner:
- Only respond with: "This is against Preptune's guidelines. Please contact support@preptune.com"
- Resume normal chat once they change topics

${memoryContext}

Remember: You're in a VOICE call, speak naturally like talking to a friend.`
        },
        ...messageHistory.slice(-10),
        {
          role: 'user',
          content: userInput
        }
      ];

      // ✅ USE SECUREAPI INSTEAD OF DIRECT FETCH
      const data = await SecureAPI.callAzureOpenAI(messages, {
        temperature: 0.8,
        max_tokens: 300,
        top_p: 0.95
      });

      return data.choices[0].message.content;

    } catch (error) {
      console.error('❌ AI response error:', error);
      return "I'm having trouble processing that. Could you try again?";
    }
  }

  /**
   * ============================================
   * GENERATE AND PLAY AUDIO
   * ============================================
   */
async generateAndPlayAudio(text, messageId, setMessages) {
  try {
    this.isSpeaking = true;
    console.log('🔊 Generating audio for:', text.substring(0, 50) + '...');

    if (!this.voiceManager) {
      console.error('❌ Voice manager not available');
      this.isSpeaking = false;
      return;
    }

    if (!this.voiceManager.synthesizer) {
      console.warn('⚠️ Synthesizer not ready, attempting initialization...');
      
      try {
        if (!this.voiceManager.isConnected) {
          console.log('⏳ Initializing voice manager...');
          await this.voiceManager.initialize();
        }

        if (!this.voiceManager.synthesizer) {
          throw new Error('Synthesizer still not available after initialization');
        }
        
        console.log('✅ Synthesizer initialized successfully');
      } catch (initError) {
        console.error('❌ Failed to initialize synthesizer:', initError);
        
        setMessages(prev => prev.map(msg => 
          msg.id === messageId 
            ? { ...msg, isStreaming: false, audioComplete: false, error: 'Audio unavailable' }
            : msg
        ));
        
        this.isSpeaking = false;
        return;
      }
    }

    try {
      await this.voiceManager.streamAudioChunks(
        text,
        this.voiceManager.selectedVoice
      );

      console.log('✅ Audio playback complete');

      setMessages(prev => prev.map(msg => 
        msg.id === messageId 
          ? { ...msg, isStreaming: false, audioComplete: true }
          : msg
      ));

      this.isSpeaking = false;

    } catch (streamError) {
      console.error('❌ Audio stream error:', streamError);
      
      setMessages(prev => prev.map(msg => 
        msg.id === messageId 
          ? { ...msg, isStreaming: false, audioComplete: false }
          : msg
      ));
      
      this.isSpeaking = false;
    }

  } catch (error) {
    console.error('❌ Audio generation error:', error);
    this.isSpeaking = false;
  }
}

  /**
   * ============================================
   * DETERMINE IF NEEDS FOLLOW-UP
   * ============================================
   */
  shouldGenerateFollowUp(response) {
    if (response.length < 100) return false;
    if (response.trim().endsWith('?')) return false;
    if (response.length > 250) return true;

    const continueIndicators = [
      'let me explain',
      'here\'s the thing',
      'what\'s interesting is',
      'another thing',
      'also',
      'additionally',
      'furthermore'
    ];

    return continueIndicators.some(phrase => 
      response.toLowerCase().includes(phrase)
    );
  }

  /**
   * ============================================
   * GENERATE FOLLOW-UP AUDIO
   * ✅ UPDATED: Uses SecureAPI
   * ============================================
   */
  async generateFollowUpAudio(previousResponse, messageHistory, setMessages) {
    console.log('🔄 Generating follow-up audio...');

    try {
      const messages = [
        {
          role: 'system',
          content: `You just said: "${previousResponse}"

Now continue this thought naturally. Add 1-2 more sentences to complete your explanation or story. Keep it conversational and natural.

**Rules:**
- Don't repeat what you already said
- Keep it brief (1-2 sentences)
- End naturally, don't force continuation
- If thought is complete, just say "That's the main point" or similar closing`
        },
        {
          role: 'user',
          content: 'Continue your previous response naturally.'
        }
      ];

      // ✅ USE SECUREAPI
      const data = await SecureAPI.callAzureOpenAI(messages, {
        temperature: 0.7,
        max_tokens: 150
      });

      const followUpText = data.choices[0].message.content;

      const followUpMsgId = Date.now();
      setMessages(prev => [...prev, {
        id: followUpMsgId,
        type: 'bot',
        content: followUpText,
        isVoiceMode: true,
        isFollowUp: true,
        isStreaming: true,
        timestamp: new Date().toISOString()
      }]);

      await this.generateAndPlayAudio(followUpText, followUpMsgId, setMessages);

      console.log('✅ Follow-up complete');

    } catch (error) {
      console.error('❌ Follow-up generation error:', error);
    }
  }

  async stopCurrentAudio() {
    console.log('⏸️ Stopping audio...');
    
    try {
      if (!this.voiceManager) {
        console.warn('⚠️ Voice manager not available');
        return;
      }

      if (typeof this.voiceManager.interruptCurrentAudio === 'function') {
        this.voiceManager.interruptCurrentAudio();
        console.log('✅ Audio stopped');
      } else if (typeof this.voiceManager.stopCurrentAudio === 'function') {
        await this.voiceManager.stopCurrentAudio();
        console.log('✅ Audio stopped');
      } else {
        console.warn('⚠️ No stop method available on voice manager');
      }

      this.isSpeaking = false;

    } catch (error) {
      console.error('❌ Error stopping audio:', error);
      this.isSpeaking = false;
    }
  }

  /**
   * ============================================
   * HANDLE TRANSITION: TEXT → VOICE
   * ✅ UPDATED: Uses SecureAPI
   * ============================================
   */
  async transitionToVoiceMode(currentMessages, setMessages) {
    console.log('🔄 Transitioning to voice mode...');

    this.isVoiceActive = true;

    const greeting = await this.generateTransitionGreeting(currentMessages);

    const greetingMsgId = Date.now();
    setMessages(prev => [...prev, {
      id: greetingMsgId,
      type: 'bot',
      content: greeting,
      isVoiceMode: true,
      isTransition: true,
      isStreaming: true,
      timestamp: new Date().toISOString()
    }]);

    await this.generateAndPlayAudio(greeting, greetingMsgId, setMessages);

    console.log('✅ Transitioned to voice mode');
  }

  /**
   * ============================================
   * GENERATE TRANSITION GREETING
   * ✅ UPDATED: Uses SecureAPI
   * ============================================
   */
  async generateTransitionGreeting(currentMessages) {
    try {
      const recentContext = currentMessages
        .slice(-5)
        .map(m => `${m.type === 'user' ? 'User' : 'You'}: ${m.content}`)
        .join('\n');

      const messages = [
        {
          role: 'system',
          content: `You were having a text conversation. User just switched to voice mode.

Recent conversation:
${recentContext}

Create a brief, natural greeting acknowledging the switch to voice. Reference what you were just discussing. Keep it casual and smooth (1-2 sentences).

Examples:
- "Hey! Good to hear your voice. So about what we were discussing..."
- "Oh nice, voice mode! Let's continue - you were asking about..."
- "Perfect timing for voice. Let me elaborate on that..."

Don't say "switching to voice mode" - just naturally continue.`
        },
        {
          role: 'user',
          content: 'Generate transition greeting'
        }
      ];

      // ✅ USE SECUREAPI
      const data = await SecureAPI.callAzureOpenAI(messages, {
        temperature: 0.8,
        max_tokens: 100
      });

      return data.choices[0].message.content;

    } catch (error) {
      console.error('❌ Transition greeting error:', error);
      return "Hey! Good to hear your voice. What's on your mind?";
    }
  }

  /**
   * ============================================
   * HANDLE TRANSITION: VOICE → TEXT
   * ============================================
   */
  async transitionToTextMode(setMessages) {
    console.log('🔄 Transitioning to text mode...');

    if (this.isSpeaking && this.voiceManager) {
      await this.voiceManager.stopCurrentAudio();
      this.isSpeaking = false;
    }

    this.isVoiceActive = false;

    console.log('✅ Transitioned to text mode');
  }

  /**
   * ============================================
   * UTILITY: Stop all audio
   * ============================================
   */
  async stopAllAudio() {
    if (this.voiceManager && this.isSpeaking) {
      await this.voiceManager.stopCurrentAudio();
      this.isSpeaking = false;
    }
  }
}

export default VoiceTextIntegrationManager;